import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import Button from '@/components/button'
import Loading from '@/components/loading'
import { useUpdateIntegrationOnPulverization } from '@/hooks/mutations/use-update-integration-on-pulverization'
import { useGetOrganizations } from '@/hooks/queries/use-get-organizations'
import { useCreatePulverizationMap } from '@/pages/diagnosis-view/contexts/create-pulverization-map-context'
import { transformUnderscoreInSpaceAndUppercaseFirstLetter } from '@/utils/format-strings'

import { useStyles } from './styles'

interface IntegrationModalProps {
  open: boolean
  onClose: () => void
  layers: string[]
  onSubmit: () => void
}

export function IntegrationModal({
  open,
  onClose,
  layers,
  onSubmit,
}: IntegrationModalProps) {
  const classes = useStyles()

  const [errors, setErrors] = useState({
    selectedOrganizationId: '',
    selectedLayers: '',
  })

  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
  const [selectedLayers, setSelectedLayers] = useState<string[]>([])

  const [notSend, setNotSend] = useState(false)
  const [promptOrgsWindow, setPromptOrgsWindow] = useState<Window | null>(null)
  const [promptAuthWindow, setPromptAuthWindow] = useState<Window | null>(null)

  const { culture, diagnosisId } = useCreatePulverizationMap()

  const updateIntegrationOnPulverizationMutation =
    useUpdateIntegrationOnPulverization(culture, diagnosisId!, 'john-deere')

  const { data, refetch, isFetching } = useGetOrganizations({
    enabled: open,
    onSuccess: (data) => {
      if (!data.authorize) return
      if (promptOrgsWindow) return
      const newWindow = window.open(
        data.auth_url,
        '_blank',
        'width=900,height=600',
      )
      setPromptOrgsWindow(newWindow)
    },
  })

  function validateInputsErrors() {
    const errors = {
      selectedOrganizationId: !selectedOrganizationId
        ? 'Selecione uma organização'
        : '',
      selectedLayers: !selectedLayers.length
        ? 'Selecione ao menos uma camada'
        : '',
    }

    if (errors.selectedOrganizationId || errors.selectedLayers) {
      setErrors(errors)
      return false
    }

    return true
  }

  function handleButtonSubmit() {
    if (notSend) {
      onSubmit()
      onClose()
      return
    }

    const isValidInputs = validateInputsErrors()

    if (!isValidInputs) return

    const organization = data?.organizations.find(
      (organization) => organization.id === selectedOrganizationId,
    )

    if (!organization?.connection) {
      const newWindow = window.open(
        organization?.connection_url,
        '_blank',
        'width=900,height=600',
      )
      return setPromptAuthWindow(newWindow)
    }

    handleSubmit()
  }

  function handleSubmit() {
    updateIntegrationOnPulverizationMutation.mutate(
      {
        organization: selectedOrganizationId,
        layers: selectedLayers.map((layer) => layer.replaceAll('-', '_')),
      },
      {
        onSuccess: () => {
          onSubmit()
          onClose()
        },
        onError: (e) => {
          const message = e instanceof Error ? e.message : 'Erro desconhecido'
          toast.error(
            `Não foi possível habilitar a integração com o Operations Center. [${message}]`,
          )
        },
      },
    )
  }

  const isLoadingOrgsIcon = useMemo(() => {
    if (isFetching || (promptOrgsWindow && !promptOrgsWindow.closed)) {
      const LoadingIcon = () => (
        <Loading
          className={classes.loadingSelect}
          size='1.8rem'
          color='secondary'
        />
      )
      return LoadingIcon
    }
  }, [isFetching, promptOrgsWindow])

  useEffect(() => {
    if (!promptOrgsWindow) return

    const interval = setInterval(() => {
      if (promptOrgsWindow?.closed) {
        clearInterval(interval)
        refetch()
      }
    })

    return () => {
      clearInterval(interval)
    }
  }, [promptOrgsWindow])

  useEffect(() => {
    if (!promptAuthWindow) return

    const interval = setInterval(() => {
      if (promptAuthWindow?.closed) {
        clearInterval(interval)
        handleSubmit()
      }
    })

    return () => {
      clearInterval(interval)
    }
  }, [promptAuthWindow])

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle disableTypography>
        <img src='assets/img/john_deere_1.svg' alt='Ícone John Deere' />
      </DialogTitle>

      <DialogContent>
        <Typography variant='subtitle2'>
          Definir organização e camadas para sincronização
        </Typography>
        <FormControl
          variant='outlined'
          color='secondary'
          size='small'
          error={Boolean(errors.selectedOrganizationId)}
        >
          <InputLabel
            className={classes.label}
            id='select-organization-input-label'
          >
            Selecionar a organização
          </InputLabel>
          <Select
            labelId='select-organization-input-label'
            value={selectedOrganizationId}
            onChange={(event) => {
              setSelectedOrganizationId(event.target.value as string)
              setErrors((state) => ({ ...state, selectedOrganizationId: '' }))
            }}
            className={classes.select}
            disabled={notSend}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
            IconComponent={isLoadingOrgsIcon}
          >
            {data?.organizations.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.selectedOrganizationId}</FormHelperText>
        </FormControl>

        <FormControl
          variant='outlined'
          color='secondary'
          hiddenLabel
          size='small'
          error={Boolean(errors.selectedLayers)}
        >
          <InputLabel id='select-layer-input-label' className={classes.label}>
            Selecionar as camadas
          </InputLabel>
          <Select
            labelId='select-layer-input-label'
            value={selectedLayers}
            defaultValue={[]}
            onChange={(e) => {
              setSelectedLayers(e.target.value as string[])
              setErrors((state) => ({ ...state, selectedLayers: '' }))
            }}
            renderValue={(selected) =>
              (selected as string[])
                .map(transformUnderscoreInSpaceAndUppercaseFirstLetter)
                .join(', ')
            }
            className={classes.select}
            multiple
            disabled={notSend}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              getContentAnchorEl: null,
              classes: { paper: classes.paper },
            }}
          >
            {layers.map((layer) => (
              <MenuItem key={layer} value={layer}>
                <Checkbox
                  checked={Boolean(
                    selectedLayers.find(
                      (selectedLayer) => selectedLayer === layer,
                    ),
                  )}
                />
                <ListItemText
                  primary={transformUnderscoreInSpaceAndUppercaseFirstLetter(
                    layer,
                  )}
                />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.selectedLayers}</FormHelperText>
        </FormControl>

        <FormControlLabel
          checked={notSend}
          onChange={(_, checked) => setNotSend(checked)}
          control={<Checkbox size='small' />}
          label={
            <Box display='flex'>
              <Typography variant='body1'>
                Não quero enviar nenhum arquivo para o Operations Center.
              </Typography>
              <Tooltip
                title='Ao selecionar essa opção seus arquivos estarão disponíveis para
                download e não serão enviados ao John Deere Operations Center.'
                placement='left'
                arrow={true}
                classes={{
                  tooltip: classes.tooltip,
                }}
              >
                <InfoOutlinedIcon />
              </Tooltip>
            </Box>
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} outlined>
          Cancelar
        </Button>
        <Button
          onClick={handleButtonSubmit}
          loading={
            updateIntegrationOnPulverizationMutation.isLoading ||
            (!!promptAuthWindow && !promptAuthWindow.closed)
          }
        >
          Concluir
        </Button>
      </DialogActions>
    </Dialog>
  )
}
